import {Configuration} from "shared/xp/config";
import {isFunction}    from "underscore";

const OFF = 0;
const ERROR = 1;
const LOG = 2;
const VERBOSE = 3;

// 0 = off, 1 = warnings/errors, 2 = log, 3 = verbose (where supported)
 // @ts-ignore
let level = Configuration.val({name: "zmagsLog", configName: "enableLogging", type: "number", defaultValue: ERROR});

// Old IE doesn't inherit the logging functions from Function. This also means that ES5-shim's bind() doesn't work
// on it. This, we cannot supply it multiple arguments at runtime then. Using emulation mode in IE 11 will fail
// this test, though, so we re-test using Error.stack, which is not present in legacy IEs - the test is only
// performed once, so it will not impact performance. Also, iOS (5) is faulty as well, only printing the first
// supplied argument, so we have to join into a string argument there as well...
let singleArgument = (typeof console !== "undefined" && typeof console.info !== "function"); // handle ios FIXME
if (!singleArgument) {
    try {
        throw new Error();
    } catch (e) {
        singleArgument = !e.stack;
    }
}

/**
 * A simple instantiable logger.
 *
 * @author Bo Gotthardt
 * @class Logger
 * @constructor
 *
 * @param {String} name The logger name.
 */
export class Logger {

    private _name: string;

    constructor(name: string){
        this._name = name + (Logger.id ? `:${Logger.id}` : "");
    }


    /** Logging off *completely*. */
    static OFF = OFF;
    /** Logging on, but only for errors. */
    static ERROR = ERROR;
    /** Logging on. */
    static LOG = LOG;
    /** Logging on, including verbose where supported. */
    static VERBOSE = VERBOSE;

    /**
     * @type {String}
     * The logger id to postfix all logs created by the current context (viewer). <p>
     *
     * This is used by the viewer to distinguish logs when more viewers are activated on the same page. It supports
     * checking the current (direct) url for an embedded/lightbox value, which by convention stores the Experience id.
     */
     // @ts-ignore
    static id = Configuration.val({name: ["embedded", "lightbox"], configName: "logId", type: "string"}); // *not* used by the Editor!

    /**
     * Sets/returns the global log level. <p>
     *
     * Note: the log level will be initially set via the <tt>zmagsLog</tt> query parameter, secondarily from the
     * configuration, but can then be changed at runtime via this method.
     *
     * Logger.OFF: logging off (no errors logged either!).
     * Logger.ERROR: logging on for warnings/errors only.
     * Logger.LOG: logging on.
     * Logger.VERBOSE: verbose (where supported by modules).
     * Logger.TIMED: verbose + timestamp for each logged statement.
     *
     * @param  {Number} [lvl] The new log level; can be null/undefined, in which case the current log level is kept.
     * @return {Number} The current global log level; defaults to the configured value.
     */
    static level = (lvl: number) => {
        if (typeof lvl === "number") {
            level = lvl;

            new Logger("Logger").info(`Set log level: ${level}`);
        }
        return level;
    };

    /**
     * Use the specified console function name to print the list of data.
     *
     * @private
     *
     * @param {String} functionName
     * @param {Arguments/ *[]} dataList
     */
    _callPrintFunction(functionName: string, dataList: string[]) {
        let messages = [`[${this._name}]`];
        const now = new Date();

        messages.push(`${now.getHours()}:${now.getMinutes()}:${now.getSeconds()}:${now.getMilliseconds()}`);
        messages = messages.concat(Array.prototype.slice.call(dataList));
        if (singleArgument) {
            // @ts-ignore
            console[functionName](messages.join(" "));
        } else {
            // @ts-ignore
            // Chrome requires the console as it's own context.
            console[functionName](...messages);
        }
    }

    /**
     * Print the list of data to the console if the lvl is met. <p>
     *
     * If there is only one argument and it is a Promise, it's resolved value will be logged instead.
     *
     * @private
     *
     * @param {Number}    lvl          The desired level to log.
     * @param {String}    functionName The console name to call, e.g. "info", "warn".
     * @param {Arguments} dataList     The data to log.
     */
    // @ts-ignore
    _print(lvl: number, functionName: string, dataList) {
        if (typeof console !== "undefined" && level >= lvl) {
            const scope = this;
            if (dataList[0] && dataList[0].promise && isFunction(dataList[0].promise) && dataList.length === 1) {
                dataList[0]
                //@ts-ignore
                    .done((...args) => {
                        scope._callPrintFunction(functionName, args);
                    })
                    .fail(() => {
                        scope._callPrintFunction(functionName, ["Promise failed."]);
                    });
            } else {
                scope._callPrintFunction(functionName, dataList);
            }
        }
    }

    /**
     * Log informational message.
     */
    info(...args: string[]) {
        this._print(LOG, "info", args);
    }

    /**
     * Log verbose message.
     */
    verbose(...args: string[]) {
        this._print(VERBOSE, "info", args);
    }

    /**
     * Log warning about undesired behavior.
     */
    warn(...args: string[]) {
        this._print(ERROR, "warn", args);
    }

    /**
     * Log error message.
     */
    error(...args: string[]) {
        this._print(ERROR, "error", args);
    }

}

// Set initial log level so we can log it...
Logger.level(level);



// WEBPACK FOOTER //
// ./src/shared/util/Logger.ts